// src/pages/CreateDepositPage.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import Card from "../components/Card";
import Button from "../components/Button";
import { Box, TextField, Typography } from "@mui/material";
import { createDeposit } from "../services/depositService";
import PageHeader from '../components/PageHeader';
import config from "../config";
import "./CreateDepositPage.css";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


function CreateDepositPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [ownerEmail, setOwnerEmail] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [ownerClientEmail, setOwnerClientEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailOwnerError, setOwnerEmailError] = useState(false);
  const [emailClientError, setClientEmailError] = useState(false);
  const [sameEmailError, setSameEmailError] = useState(false);

  
  const showErrorOnDepositField = depositAmount !== "" && (depositAmount < 20 || depositAmount > 2000);
  const handleOwnerEmailChange = (e) => {
      const email = e.target.value;
      setOwnerEmail(email);
      setOwnerEmailError(email !== "" && !emailRegex.test(email));
      setSameEmailError(email !== "" && email === ownerClientEmail);
    };
    
  const handleClientEmailChange = (e) => {
      const email = e.target.value;
      setOwnerClientEmail(email);
      setClientEmailError(email !== "" && !emailRegex.test(email));
      setSameEmailError(email !== "" && email === ownerEmail);
    };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!ownerEmail || !depositAmount || !ownerClientEmail) {
      setError(t("createDeposit.errorEmptyFields"));
      return;
    }

    if (!emailRegex.test(ownerEmail) || !emailRegex.test(ownerClientEmail)) {
      setError(t("createDeposit.errorInvalidEmail"));
      return;
    }

    if (ownerEmail.toLowerCase() === ownerClientEmail.toLowerCase()) {
      setError(t("createDeposit.errorSameEmail"));
      return;
    }

    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
      setError(t("createDeposit.captchaNotLoaded"));
      return;
    }

    try {
      setLoading(true);
      const recaptchaToken = await window.grecaptcha.enterprise.execute(
        config.RECAPTCHA_PUBLIC_KEY,
        { action: "create_deposit" }
      );

      const result = await createDeposit(ownerEmail, depositAmount, ownerClientEmail, recaptchaToken);
      if (result && result.depositId) {
        const depositData = {
          depositId: result.depositId,
          isOwnerNameProvided: result.isOwnerNameProvided || false,
        };
        localStorage.setItem("depositData", JSON.stringify(depositData));
        navigate("/createDepositSuccess");
      } else {
        setError(t("createDeposit.errorResponse"));
      }
    } catch (err) {
      setError(`${t("createDeposit.errorPrefix")} ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <PageHeader 
        title={t("createDeposit.title")}
        subtitle={t("createDeposit.subtitle")}
      />
      
      <Card 
        title={t("createDeposit.cardClientTitle")}
        subtitle={t("createDeposit.cardClientSubtitle")}
        >
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label={t("createDeposit.ownerClientEmailLabel")}
            variant="outlined"
            fullWidth
            margin="normal"
            value={ownerClientEmail}
            onChange={handleClientEmailChange}
            required
            error={emailClientError || sameEmailError}
            helperText={
            emailClientError
                 ? t("createDeposit.errorInvalidEmail")
                 : sameEmailError
                   ? t("createDeposit.errorSameEmail")
                   : " "
             }
            className="rounded-textfield"
            disabled={loading}
          />
        </Box>
      </Card>

      <Card 
        title={t("createDeposit.cardOwnerTitle")} 
        subtitle={t("createDeposit.cardOwnerSubtitle")} 
        >
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label={t("createDeposit.ownerEmailLabel")}
            variant="outlined"
            fullWidth
            margin="normal"
            value={ownerEmail}
            onChange={handleOwnerEmailChange}
            required
            error={emailOwnerError || sameEmailError}
            helperText={
              emailOwnerError
                ? t("createDeposit.errorInvalidEmail")
                : sameEmailError
                  ? t("createDeposit.errorSameEmail")
                  : " "
            }
            className="rounded-textfield"
            disabled={loading}
          />
          <TextField
            label={t("createDeposit.depositAmountLabel")}
            variant="outlined"
            fullWidth
            margin="normal"
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            required
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]+([.,][0-9]+)?",
              min: "20",
              max: "2000",
              step: "1",
            }}
            error={showErrorOnDepositField}
            helperText={showErrorOnDepositField && t("createDeposit.errorTextfieldDeposit")}
            className="rounded-textfield"
            disabled={loading}
          />
        </Box>
      </Card>

      <div className="submit-container">
        <Button
          label={loading ? t("createDeposit.loading") : t("createDeposit.submitButton")}
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
        />
        {error && <p className="error-message">{error}</p>}
      </div>
      
    </Layout>
  );
}

export default CreateDepositPage;